//LIBRERIES
import { computed, createApp, defineComponent, onMounted, reactive, ref, Ref, toRefs } from 'vue'
import { i18n } from '../lang/index';
import orderBy from "lodash/orderBy";
import deburr from "lodash/deburr";
import moment from "../node_modules/moment/moment";
import { useI18n } from 'vue-i18n';

//MODELS
import { App } from "../domain/App";
import { Boep } from "../domain/Boep";
import { OepDocument } from "../domain/OepDocument";
import { Entity } from "../domain/Entity";
import { Publication } from "../domain/Publication";
import { Cookie } from "../domain/Cookie";
import { ProcessType } from "../domain/enum/ProcessType";
import { Languages } from "../domain/enum/Languages";
import { Constants } from "../domain/enum/Constants";
import ICommonDataState from '../domain/interfaces/ICommonDataState';

//APISERVICES
import { getBoepList, getDocumentsById, getEntityById } from "../infrastructure/apiServices/boep.service";
import { getLastPublicationsList } from "../infrastructure/apiServices/publications.service";
import { getHeaderConfiguration, getClientAvailableLanguajes, getFeatureFlag } from "../infrastructure/apiServices/masterData.service";

//COMPOSABLES
import { useCommonData } from "./composables/useCommonData";
import { useIncidences } from "./composables/useIncidences";

export const BoepComponent = defineComponent({
  setup() {
    // dependencies
    const { t, locale } = useI18n(); 
    
    // states
    const commonDataState = reactive({
      headerConfig: {},
      availableLanguajes: { defaultLanguage: '', secondLanguage: ''},
      cookie: new Cookie(),
      app: new App(),
      locale
    });
    
    // computed properties
    const pageTitle = computed(() => t('lang.shared.boep'));
    const currentLanguage = computed(() => commonDataState.locale);
    const lastLogin = computed(() => moment(commonDataState.app.profile.lastLogin[0]).format(commonDataState.app.formatDate) + ' ' + moment.utc(commonDataState.app.profile.lastLogin[0]).local().format('HH:mm:ss'));
    const boepsFiltered = computed(() => boeps.value.filter(boep => boep.documents.length > 0));

    // shared properties
    const imageClass = 'applicant-hero-image--boep';
    const isLoading = ref(false);
    const showMandatoryCookies = ref(false);
    const showAnalysisCookies = ref(false);
    const showModalConfigurateCookies = ref(false);
    const showServiceStopModal = ref(false);
    const dontShowAgainStopServiceModalAgain = ref(false);
    const serviceStopLocalStorageKey = 'dontShowServiceStopModal';
    const serviceStopEndDate = moment('2025-02-19T18:30:00');

    // view properties
    const lastPublications = ref([]);
    const boeps: Ref<Boep[]> = ref([]);
    // methods
    const getBoepListOrdered = () => {
      boeps.value = orderBy(boeps.value, ['year', boep => deburr(boep.title[currentLanguage.value]).toLowerCase()], ['desc', 'asc']);
    };

    const getBoep = async () => {
      isLoading.value = true;
      try {
        const response: any = await getBoepList();
        for (const element of response.data) {
          let boep = new Boep(element.id, element.title, element.description, element.year);
          await gettingEntityById(element.entityOepId, boep);
          await gettingDocumentsById(boep);
          boeps.value.push(boep);
        }
        getBoepListOrdered();
      } catch (error) {
        console.error(error);
      } finally {
        isLoading.value = false;
      }
    };

    const gettingEntityById = async (id, boep) => {
      try {
        const response: any = await getEntityById(id);
        let entity = new Entity(response.data.id, response.data.name);
        boep.entity = entity;
      } catch (error) {
        console.error(error);
      }
    };

    const gettingDocumentsById = async (boep) => {
      try {
        const response: any = await getDocumentsById(boep.id);
        for (const element of response.data) {
          let document = new OepDocument(element);
          boep.addDocuments(document);
        }
      } catch (error) {
        console.error(error);
      }
    };

    const getLastPublicationsListOrdered = () => {
      lastPublications.value = orderBy(lastPublications.value, ['date', lastPublication => deburr(lastPublication.title[currentLanguage.value]).toLowerCase()], ['desc', 'asc']);
    };

    const getAsideData = () => {
      isLoading.value = true;
      getLastPublicationsList(Constants.publications.AMOUNT_OF_PUBLICATIONS).then((response: { data: [] }) => {
        response.data.forEach(element => {
          lastPublications.value.push(new Publication(element));
        });
        getLastPublicationsListOrdered();
        isLoading.value = false;
      }).catch(() => {
        isLoading.value = false;
      });
    };

    const getAllData = () => {
      getBoep();
      getAsideData();
      commonDataState.app.getProfile();
    };

    const saveCookies = () => {
      commonDataState.cookie.savePersonalizateCookies();
      showModalConfigurateCookies.value = false;
    };

    const closeModalConfigurateCookies = () => {
      showModalConfigurateCookies.value = false;
    };

    const acceptAllCookies = () => {
      commonDataState.cookie.acceptAllCookies();
    };

    const declineAllCookies = () => {
      commonDataState.cookie.declineAllCookies();
    };

    const downloadDocument = (url) => {
      location.href = url;
    };

    const changeLocal = (language) => {
      commonDataState.locale = language;
      commonDataState.app.changeLanguaje(language);
      getBoepListOrdered();
      getLastPublicationsListOrdered();
    };

    const closeStopServiceModal = () => {
      showServiceStopModal.value = false;
    };

    const acceptStopServiceModal = () => {
      if (dontShowAgainStopServiceModalAgain.value) {
        const serviceStopData = { serviceStopNotificationReaded: dontShowAgainStopServiceModalAgain.value, expires: serviceStopEndDate };
        localStorage.setItem(serviceStopLocalStorageKey, JSON.stringify(serviceStopData));
      }
      showServiceStopModal.value = false;
    };

    const checkModalServiceStopLocalStorage = () => {
      const serviceStopItem = localStorage.getItem(serviceStopLocalStorageKey);
      if (!serviceStopItem) {
        return;
      }
      const serviceStopItemData = JSON.parse(serviceStopItem);
      if (moment() > moment(serviceStopItemData.expires)) {
        localStorage.removeItem(serviceStopLocalStorageKey);
      }
    };

    const updateCommonData = (state: ICommonDataState) => {
      commonDataState.availableLanguajes = state.availableLanguajes;
      commonDataState.cookie = state.cookie;
      commonDataState.headerConfig = state.headerConfig;
      commonDataState.app = state.app;
      commonDataState.locale = state.locale;

      getAllData();
    };
    
    // composables
    const useCommonDataDependencies = {
      updateCommonData,
      getHeaderConfiguration,
      getFeatureFlag,
      getClientAvailableLanguajes,
    };
    const { loadCommonData } = useCommonData(useCommonDataDependencies);
    const { incidence, showNotificationIncidence, IncidencesTypes, closeIncidence} = useIncidences();

    // lifecycle hooks
    onMounted(() => {
      loadCommonData();
      checkModalServiceStopLocalStorage();
      showServiceStopModal.value = commonDataState.app.isServiceStopActive && (!localStorage.getItem(serviceStopLocalStorageKey));
    });

    return {
      // dependencies
      t,
      ProcessType: ProcessType,
      Languages: Languages,
      constants: Constants,
      // states
      ...toRefs(commonDataState),
      // shared view properties
      showServiceStopModal,
      showModalConfigurateCookies,
      dontShowAgainStopServiceModalAgain,
      serviceStopEndDate,
      showMandatoryCookies,
      showAnalysisCookies,
      imageClass,
      lastPublications,
      // computed properties
      lastLogin,
      pageTitle,
      currentLanguage,
      boepsFiltered,
      // methods
      getAllData,
      saveCookies,
      closeModalConfigurateCookies,
      acceptAllCookies,
      declineAllCookies,
      changeLocal,
      acceptStopServiceModal,
      closeStopServiceModal,
      downloadDocument,
      getBoep,
      getBoepListOrdered,
      gettingEntityById,
      gettingDocumentsById,
      getAsideData,
      getLastPublicationsListOrdered,
      closeIncidence,
      // view properties
      boeps,
      isLoading,
      incidence,
      showNotificationIncidence,
      IncidencesTypes
    };
  },
});

const vueApp = createApp(BoepComponent);
vueApp.use(i18n);
vueApp.config.compilerOptions.whitespace = 'preserve';
vueApp.mount(Constants.APP);
